import { useState, useMemo } from 'react';
import { Link } from 'react-router';
import {
  Loader2,
  Download,
  BarChart3,
  Users,
  CheckCircle2,
  TrendingUp,
  ScrollText,
  Search,
  BookOpen,
  FileCheck,
  Activity,
  Layers,
  ChevronLeft,
  ChevronRight,
  ArrowRight,
  ClipboardList,
  Sparkles,
} from 'lucide-react';
import { usePrograms, useProgramsOverview, useProgramReport } from '../hooks/usePrograms';
import { StatusChip } from '@lms/ui';
import { copy } from '../copy/copy';
import { Bilingual } from '../components/Bilingual';
import { useLanguage } from '../lib/LanguageContext';
import { TranscriptPreviewModal } from '../components/TranscriptPreviewModal';
import { getGradeLetter, getGradePredikat } from '@lms/shared';

export function Reporting() {
  const { data: programsData, isLoading: loadingPrograms } = usePrograms({ limit: 100 });
  const { data: overview, isLoading: loadingOverview } = useProgramsOverview();

  const [selectedProgramId, setSelectedProgramId] = useState<string>('');
  const [activeTab, setActiveTab] = useState<'PROGRESS' | 'TRANSCRIPTS'>('PROGRESS');
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('ALL');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  const [selectedModalStudent, setSelectedModalStudent] = useState<{
    userId: string;
    programId: string;
  } | null>(null);

  const { language, t } = useLanguage();

  const { data: report, isLoading: loadingReport } = useProgramReport(selectedProgramId);
  const programs = programsData?.items || [];

  // Filter participants
  const filteredParticipants = useMemo(() => {
    if (!report?.participants) return [];
    return report.participants.filter(p => {
      const matchesSearch =
        searchQuery.trim() === '' ||
        p.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        p.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (p.userCode && p.userCode.toLowerCase().includes(searchQuery.toLowerCase()));

      const matchesStatus =
        statusFilter === 'ALL' ||
        (statusFilter === 'COMPLETED' && p.status === 'COMPLETED') ||
        (statusFilter === 'ACTIVE' && p.status === 'ACTIVE');

      return matchesSearch && matchesStatus;
    });
  }, [report, searchQuery, statusFilter]);

  // Paginated participants for fast, smooth 60fps DOM rendering
  const totalPages = Math.ceil(filteredParticipants.length / pageSize) || 1;
  const paginatedParticipants = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return filteredParticipants.slice(start, start + pageSize);
  }, [filteredParticipants, currentPage, pageSize]);

  // Reset pagination on filter or program change
  const handleProgramChange = (progId: string) => {
    setSelectedProgramId(progId);
    setCurrentPage(1);
  };

  const handleSearchChange = (val: string) => {
    setSearchQuery(val);
    setCurrentPage(1);
  };

  const handleStatusFilterChange = (val: string) => {
    setStatusFilter(val);
    setCurrentPage(1);
  };

  if (loadingPrograms && !programsData) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] space-y-4">
        <Loader2 className="w-8 h-8 text-primary animate-spin" />
        <p className="text-sm font-body text-on-surface-variant">
          <Bilingual {...copy.reporting.loadingPrograms} />
        </p>
      </div>
    );
  }

  const handleExport = () => {
    if (!report) return;

    const headers = ['NIP', 'Name', 'Email', 'Status', 'Progress', 'Average Score', 'Last Active'];
    const rows = filteredParticipants.map(p => [
      p.userCode || '-',
      `"${p.name.replace(/"/g, '""')}"`,
      p.email,
      p.status,
      `${Math.round(p.progress)}%`,
      p.averageScore != null ? p.averageScore : '-',
      new Date(p.lastActive).toLocaleDateString(),
    ]);

    const csvContent = [headers.join(','), ...rows.map(row => row.join(','))].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute(
      'download',
      `report-${selectedProgramId || 'all'}-${activeTab.toLowerCase()}.csv`
    );
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="space-y-8 animate-fadeIn pb-16">
      {/* Header Bar */}
      <section className="flex flex-col md:flex-row justify-between items-start md:items-end gap-6 pb-6 border-b border-outline-variant/10">
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            <span className="px-2.5 py-0.5 rounded-full text-[10px] font-black uppercase tracking-wider bg-primary/10 text-primary flex items-center gap-1">
              <Sparkles size={12} />
              Intelligence & Analytics
            </span>
          </div>
          <h1 className="text-3xl sm:text-4xl font-headline font-extrabold text-on-surface tracking-tight leading-none m-0">
            <Bilingual {...copy.reporting.listTitle} />
          </h1>
          <p className="text-sm sm:text-base font-body text-on-surface-variant max-w-2xl">
            <Bilingual {...copy.reporting.listSubtitle} />
          </p>
        </div>

        {/* Program Selector & Actions */}
        <div className="flex flex-wrap items-center gap-3 w-full md:w-auto">
          <select
            className="bg-surface-container-lowest border border-outline-variant/15 rounded-xl px-4 py-2.5 font-body text-xs sm:text-sm focus:ring-2 focus:ring-primary/20 outline-none min-w-[240px] shadow-level1 font-bold text-on-surface"
            value={selectedProgramId}
            onChange={e => handleProgramChange(e.target.value)}
          >
            <option value="">
              {language === 'ar'
                ? `🌐 ${copy.reporting.overviewTitle.ar}`
                : `🌐 ${copy.reporting.overviewTitle.id}`}
            </option>
            {programs.map(p => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </select>

          {selectedProgramId && (
            <button
              onClick={handleExport}
              disabled={!report}
              className="bg-primary text-on-primary h-11 px-5 rounded-xl flex items-center gap-2 text-xs font-label font-bold uppercase tracking-wider shadow-level1 hover:bg-primary-container active:scale-95 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Download size={15} />
              <Bilingual {...copy.reporting.btnExport} />
            </button>
          )}
        </div>
      </section>

      {/* ============================================================ */}
      {/* 1. DEFAULT GLOBAL INTELLIGENCE DASHBOARD (When no program selected) */}
      {/* ============================================================ */}
      {!selectedProgramId ? (
        loadingOverview ? (
          <div className="flex flex-col items-center justify-center min-h-[350px] space-y-4">
            <Loader2 className="w-8 h-8 text-primary animate-spin" />
            <p className="text-sm font-body text-on-surface-variant">
              <Bilingual {...copy.reporting.synthesizing} />
            </p>
          </div>
        ) : overview ? (
          <div className="space-y-8 animate-fadeIn">
            {/* Global KPI Matrix */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="bg-surface-container-lowest p-6 rounded-3xl shadow-level1 border border-outline-variant/10 relative overflow-hidden">
                <div className="flex items-center justify-between text-primary mb-3">
                  <span className="text-[10px] font-label font-bold uppercase tracking-wider text-on-surface-variant">
                    <Bilingual {...copy.reporting.globalActiveStudents} />
                  </span>
                  <Users size={20} />
                </div>
                <div className="text-3xl font-headline font-black text-on-surface">
                  {overview.stats.activeParticipants.toLocaleString()}
                </div>
                <div className="text-[11px] text-on-surface-variant mt-1.5 font-medium">
                  Dari {overview.stats.totalParticipants.toLocaleString()} total pendaftar
                </div>
              </div>

              <div className="bg-surface-container-lowest p-6 rounded-3xl shadow-level1 border border-outline-variant/10 relative overflow-hidden">
                <div className="flex items-center justify-between text-secondary mb-3">
                  <span className="text-[10px] font-label font-bold uppercase tracking-wider text-on-surface-variant">
                    <Bilingual {...copy.reporting.globalMaterials} />
                  </span>
                  <BookOpen size={20} />
                </div>
                <div className="text-3xl font-headline font-black text-on-surface">
                  {overview.stats.activeMaterials.toLocaleString()}
                </div>
                <div className="text-[11px] text-on-surface-variant mt-1.5 font-medium">
                  Modul & sesi aktif di seluruh program
                </div>
              </div>

              <div className="bg-surface-container-lowest p-6 rounded-3xl shadow-level1 border border-outline-variant/10 relative overflow-hidden">
                <div className="flex items-center justify-between text-success mb-3">
                  <span className="text-[10px] font-label font-bold uppercase tracking-wider text-on-surface-variant">
                    <Bilingual {...copy.reporting.globalCompletion} />
                  </span>
                  <CheckCircle2 size={20} />
                </div>
                <div className="text-3xl font-headline font-black text-success">
                  {overview.stats.completionRate}%
                </div>
                <div className="text-[11px] text-on-surface-variant mt-1.5 font-medium">
                  {overview.stats.completedFinalEvaluations.toLocaleString()} ujian kelulusan tuntas
                </div>
              </div>

              <div className="bg-surface-container-lowest p-6 rounded-3xl shadow-level1 border border-outline-variant/10 relative overflow-hidden">
                <div className="flex items-center justify-between text-amber-600 mb-3">
                  <span className="text-[10px] font-label font-bold uppercase tracking-wider text-on-surface-variant">
                    <Bilingual {...copy.reporting.globalPendingRegistrations} />
                  </span>
                  <ClipboardList size={20} />
                </div>
                <div className="text-3xl font-headline font-black text-amber-600">
                  {overview.stats.pendingRegistrations.toLocaleString()}
                </div>
                <div className="text-[11px] text-on-surface-variant mt-1.5 font-medium">
                  {overview.stats.pendingToday} pendaftaran baru hari ini
                </div>
              </div>
            </div>

            {/* Program Comparative Grid & Recent Activity */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Top Programs Performance List (2 cols) */}
              <div className="lg:col-span-2 bg-surface-container-lowest rounded-3xl p-6 sm:p-7 shadow-level1 border border-outline-variant/10 space-y-5">
                <div className="flex items-center justify-between">
                  <h2 className="text-base font-headline font-bold text-on-surface flex items-center gap-2 m-0">
                    <Layers size={18} className="text-primary" />
                    <span>
                      <Bilingual {...copy.reporting.topProgramsTitle} />
                    </span>
                  </h2>
                  <span className="text-xs font-mono text-on-surface-variant">
                    {overview.chartData?.length || 0} Program Aktif
                  </span>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
                  {(overview.chartData || []).map(prog => (
                    <div
                      key={prog.id || prog.name}
                      onClick={() => prog.id && handleProgramChange(prog.id)}
                      className="p-4 rounded-2xl bg-surface-container-low/40 hover:bg-surface-container-low transition-all border border-outline-variant/10 cursor-pointer space-y-3 group"
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div>
                          <span className="text-[10px] font-mono font-bold text-primary block uppercase">
                            {prog.code || 'PROGRAM'}
                          </span>
                          <h4 className="font-headline font-bold text-on-surface text-sm line-clamp-1 group-hover:text-primary transition-colors m-0">
                            {prog.fullName || prog.name}
                          </h4>
                        </div>
                        <span className="p-1 rounded-lg bg-surface-container text-on-surface-variant group-hover:text-primary group-hover:translate-x-0.5 transition-all">
                          <ArrowRight size={14} />
                        </span>
                      </div>

                      <div className="space-y-1.5 pt-1">
                        <div className="flex items-center justify-between text-[11px] font-body text-on-surface-variant">
                          <span>{prog.participants} Peserta</span>
                          <span className="font-mono font-bold text-primary">
                            {prog.completion}% Lulus
                          </span>
                        </div>
                        <div className="h-1.5 w-full bg-surface-container-high rounded-full overflow-hidden">
                          <div
                            className="h-full bg-primary transition-all duration-500"
                            style={{ width: `${Math.min(prog.completion, 100)}%` }}
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Real-time Activity Timeline (1 col) */}
              <div className="bg-surface-container-lowest rounded-3xl p-6 sm:p-7 shadow-level1 border border-outline-variant/10 space-y-5">
                <h2 className="text-base font-headline font-bold text-on-surface flex items-center gap-2 m-0">
                  <Activity size={18} className="text-secondary" />
                  <span>
                    <Bilingual {...copy.reporting.recentActivityTitle} />
                  </span>
                </h2>

                <div className="space-y-3 max-h-[340px] overflow-y-auto pr-1">
                  {(overview.activities || []).map(act => (
                    <div
                      key={act.id}
                      className="p-3 rounded-xl bg-surface-container-low/30 border border-outline-variant/5 text-xs space-y-1"
                    >
                      <div className="flex items-center justify-between">
                        <span className="font-bold text-on-surface truncate">{act.actor}</span>
                        <span className="text-[10px] font-mono text-on-surface-variant opacity-70">
                          {act.timestamp}
                        </span>
                      </div>
                      <p className="text-[11px] text-on-surface-variant font-body line-clamp-2 m-0">
                        {act.description}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Callout to select program */}
            <div className="p-6 rounded-3xl bg-primary/5 border border-primary/20 text-center space-y-2">
              <p className="text-xs sm:text-sm font-headline font-bold text-primary m-0">
                <Bilingual {...copy.reporting.btnSelectProgramPrompt} />
              </p>
            </div>
          </div>
        ) : null
      ) : null}

      {/* ============================================================ */}
      {/* 2. SPECIFIC PROGRAM COHORT REPORT */}
      {/* ============================================================ */}
      {selectedProgramId && (
        <>
          {loadingReport ? (
            <div className="flex flex-col items-center justify-center min-h-[350px] space-y-4">
              <Loader2 className="w-9 h-9 text-primary animate-spin" />
              <p className="text-sm font-body text-on-surface-variant font-medium">
                <Bilingual {...copy.reporting.synthesizing} />
              </p>
            </div>
          ) : report ? (
            <div className="space-y-8 animate-fadeIn">
              {/* Tab Selection */}
              <div className="flex items-center gap-3 border-b border-outline-variant/10 pb-4">
                <button
                  type="button"
                  onClick={() => setActiveTab('PROGRESS')}
                  className={`px-5 py-2.5 rounded-xl font-headline font-bold text-xs sm:text-sm flex items-center gap-2 transition-all ${
                    activeTab === 'PROGRESS'
                      ? 'bg-primary text-on-primary shadow-level1'
                      : 'bg-surface-container-low text-on-surface-variant hover:bg-surface-container-high'
                  }`}
                >
                  <TrendingUp size={16} />
                  <span>
                    <Bilingual {...copy.reporting.tabProgress} />
                  </span>
                </button>

                <button
                  type="button"
                  onClick={() => setActiveTab('TRANSCRIPTS')}
                  className={`px-5 py-2.5 rounded-xl font-headline font-bold text-xs sm:text-sm flex items-center gap-2 transition-all ${
                    activeTab === 'TRANSCRIPTS'
                      ? 'bg-primary text-on-primary shadow-level1'
                      : 'bg-surface-container-low text-on-surface-variant hover:bg-surface-container-high'
                  }`}
                >
                  <ScrollText size={16} />
                  <span>
                    <Bilingual {...copy.reporting.tabTranscripts} />
                  </span>
                </button>
              </div>

              {/* KPI Matrix */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
                <div className="bg-surface-container-lowest p-6 sm:p-7 rounded-3xl shadow-level1 border border-outline-variant/10 relative overflow-hidden">
                  <div className="flex items-center justify-between text-primary mb-4">
                    <span className="text-[10px] font-label font-bold uppercase tracking-wider text-on-surface-variant">
                      <Bilingual {...copy.reporting.kpiActiveNodes} />
                    </span>
                    <Users size={20} />
                  </div>
                  <div className="text-4xl font-headline font-black text-on-surface">
                    {report.summary.totalParticipants}
                  </div>
                  <div className="text-xs text-on-surface-variant mt-2 font-medium">
                    <Bilingual {...copy.reporting.kpiActiveNodesSub} />
                  </div>
                </div>

                <div className="bg-surface-container-lowest p-6 sm:p-7 rounded-3xl shadow-level1 border border-outline-variant/10 relative overflow-hidden">
                  <div className="flex items-center justify-between text-success mb-4">
                    <span className="text-[10px] font-label font-bold uppercase tracking-wider text-on-surface-variant">
                      <Bilingual {...copy.reporting.kpiConvergence} />
                    </span>
                    <CheckCircle2 size={20} />
                  </div>
                  <div className="text-4xl font-headline font-black text-success">
                    {report.summary.completionPercentage}%
                  </div>
                  <div className="text-xs text-on-surface-variant mt-2 font-medium">
                    <Bilingual {...copy.reporting.kpiConvergenceSub} />
                  </div>
                </div>

                <div className="bg-surface-container-lowest p-6 sm:p-7 rounded-3xl shadow-level1 border border-outline-variant/10 relative overflow-hidden">
                  <div className="flex items-center justify-between text-secondary mb-4">
                    <span className="text-[10px] font-label font-bold uppercase tracking-wider text-on-surface-variant">
                      <Bilingual {...copy.reporting.kpiIntegrity} />
                    </span>
                    <TrendingUp size={20} />
                  </div>
                  <div className="text-4xl font-headline font-black text-on-surface">
                    {report.summary.averageScore} / 100
                  </div>
                  <div className="text-xs text-on-surface-variant mt-2 font-medium">
                    <Bilingual {...copy.reporting.kpiIntegritySub} />
                  </div>
                </div>
              </div>

              {/* Progress Distribution & Stage Overview (Visible in PROGRESS tab) */}
              {activeTab === 'PROGRESS' && (
                <div className="space-y-6">
                  {/* Distribution Cards */}
                  <div className="bg-surface-container-lowest rounded-3xl p-6 sm:p-8 shadow-level1 border border-outline-variant/10 space-y-4">
                    <div className="flex items-center justify-between">
                      <h3 className="text-sm font-headline font-bold text-on-surface flex items-center gap-2 m-0">
                        <BarChart3 size={16} className="text-primary" />
                        <span>
                          <Bilingual {...copy.reporting.distributionTitle} />
                        </span>
                      </h3>
                    </div>

                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
                      <div className="p-4 rounded-2xl bg-surface-container-low/60 space-y-1">
                        <span className="text-[10px] font-label font-bold text-on-surface-variant uppercase">
                          <Bilingual {...copy.reporting.tier0_25} />
                        </span>
                        <div className="text-2xl font-headline font-black text-on-surface">
                          {report.summary.distribution?.tier0_25 || 0} Siswa
                        </div>
                      </div>

                      <div className="p-4 rounded-2xl bg-surface-container-low/60 space-y-1">
                        <span className="text-[10px] font-label font-bold text-on-surface-variant uppercase">
                          <Bilingual {...copy.reporting.tier26_50} />
                        </span>
                        <div className="text-2xl font-headline font-black text-on-surface">
                          {report.summary.distribution?.tier26_50 || 0} Siswa
                        </div>
                      </div>

                      <div className="p-4 rounded-2xl bg-surface-container-low/60 space-y-1">
                        <span className="text-[10px] font-label font-bold text-on-surface-variant uppercase">
                          <Bilingual {...copy.reporting.tier51_75} />
                        </span>
                        <div className="text-2xl font-headline font-black text-on-surface">
                          {report.summary.distribution?.tier51_75 || 0} Siswa
                        </div>
                      </div>

                      <div className="p-4 rounded-2xl bg-success/10 space-y-1">
                        <span className="text-[10px] font-label font-bold text-success uppercase">
                          <Bilingual {...copy.reporting.tier76_100} />
                        </span>
                        <div className="text-2xl font-headline font-black text-success">
                          {report.summary.distribution?.tier76_100 || 0} Siswa
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Stage Overview Cards */}
                  {report.stages && report.stages.length > 0 && (
                    <div className="bg-surface-container-lowest rounded-3xl p-6 sm:p-8 shadow-level1 border border-outline-variant/10 space-y-4">
                      <h3 className="text-sm font-headline font-bold text-on-surface flex items-center gap-2 m-0">
                        <BookOpen size={16} className="text-primary" />
                        <span>
                          <Bilingual {...copy.reporting.stageBottleneckTitle} />
                        </span>
                      </h3>

                      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
                        {report.stages.map(stg => (
                          <div
                            key={stg.id}
                            className="p-4 rounded-2xl bg-surface-container-low/40 border border-outline-variant/10 space-y-2 text-xs"
                          >
                            <div className="flex items-center justify-between">
                              <span className="text-[10px] font-bold text-secondary uppercase">
                                {stg.code ? `Tahap ${stg.code}` : stg.levelName}
                              </span>
                              <span className="text-[10px] font-mono text-on-surface-variant">
                                KKM: {stg.passingScore}%
                              </span>
                            </div>
                            <h4 className="font-headline font-bold text-on-surface text-sm line-clamp-1 m-0">
                              {stg.name}
                            </h4>
                            <div className="flex items-center justify-between text-[11px] text-on-surface-variant pt-1 border-t border-outline-variant/10">
                              <span>{stg.subjectsCount} Mapel</span>
                              <span>{stg.sessionsCount} Sesi</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Search & Filter Controls */}
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3">
                <div className="relative flex-1 max-w-md">
                  <Search
                    size={16}
                    className="absolute left-4 top-1/2 -translate-y-1/2 text-on-surface-variant/60"
                  />
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={e => handleSearchChange(e.target.value)}
                    placeholder={
                      language === 'ar'
                        ? copy.reporting.searchParticipant.ar
                        : copy.reporting.searchParticipant.id
                    }
                    className="w-full bg-surface-container-lowest pl-11 pr-4 py-2.5 rounded-xl border border-outline-variant/15 text-xs sm:text-sm font-body outline-none focus:ring-2 focus:ring-primary/20 shadow-sm"
                  />
                </div>

                <div className="flex items-center gap-2">
                  <select
                    value={statusFilter}
                    onChange={e => handleStatusFilterChange(e.target.value)}
                    aria-label="Filter Status Peserta"
                    className="bg-surface-container-lowest border border-outline-variant/15 rounded-xl px-4 py-2.5 font-body text-xs sm:text-sm outline-none shadow-sm font-bold"
                  >
                    <option value="ALL">
                      {language === 'ar'
                        ? copy.reporting.filterAll.ar
                        : copy.reporting.filterAll.id}
                    </option>
                    <option value="COMPLETED">
                      {language === 'ar'
                        ? copy.reporting.filterCompleted.ar
                        : copy.reporting.filterCompleted.id}
                    </option>
                    <option value="ACTIVE">
                      {language === 'ar'
                        ? copy.reporting.filterActive.ar
                        : copy.reporting.filterActive.id}
                    </option>
                  </select>
                </div>
              </div>

              {/* Trajectory & Grades Table */}
              <div className="bg-surface-container-lowest rounded-3xl overflow-hidden shadow-level2 border border-outline-variant/10">
                <div className="px-6 sm:px-8 py-5 border-b border-outline-variant/10 bg-surface-container-low/30 flex items-center justify-between">
                  <h3 className="text-xs font-label font-black uppercase tracking-wider text-primary m-0">
                    {activeTab === 'PROGRESS' ? (
                      <Bilingual {...copy.reporting.trajectoryTitle} />
                    ) : (
                      <Bilingual {...copy.reporting.tabTranscripts} />
                    )}
                  </h3>
                  <span className="text-xs font-mono font-bold text-on-surface-variant">
                    {filteredParticipants.length} Peserta
                  </span>
                </div>

                <div className="overflow-x-auto">
                  <table className="w-full text-left border-collapse">
                    <thead>
                      <tr className="bg-surface-container-low/60 border-b border-outline-variant/10 text-[10px] font-label font-black uppercase tracking-wider text-on-surface-variant">
                        <th className="px-6 py-4">
                          <Bilingual {...copy.reporting.tableHeaderEntity} />
                        </th>
                        <th className="px-6 py-4">
                          <Bilingual {...copy.reporting.tableHeaderContact} />
                        </th>
                        <th className="px-6 py-4 text-center">
                          <Bilingual {...copy.reporting.tableHeaderState} />
                        </th>
                        <th className="px-6 py-4 text-center">
                          <Bilingual {...copy.reporting.tableHeaderProgress} />
                        </th>
                        <th className="px-6 py-4 text-center">
                          <Bilingual {...copy.reporting.tableHeaderAverageScore} />
                        </th>
                        <th className="px-6 py-4 text-center">
                          <Bilingual {...copy.reporting.tableHeaderAction} />
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-outline-variant/10 text-xs font-body">
                      {paginatedParticipants.map(p => {
                        const avg =
                          p.averageScore != null ? p.averageScore : Math.round(p.progress);
                        const pred = getGradePredikat(avg);
                        const letter = getGradeLetter(avg);

                        return (
                          <tr
                            key={p.id}
                            className="hover:bg-surface-container-low/40 transition-colors"
                          >
                            <td className="px-6 py-4">
                              <Link to={`/participants/${p.id}`} className="block group">
                                <div className="font-headline font-bold text-on-surface group-hover:text-primary transition-colors">
                                  {p.name}
                                </div>
                                {p.userCode && (
                                  <span className="text-[10px] font-mono text-on-surface-variant">
                                    NIP: {p.userCode}
                                  </span>
                                )}
                              </Link>
                            </td>

                            <td className="px-6 py-4 text-on-surface-variant">
                              <div>{p.email}</div>
                              {p.phone && <span className="text-[10px] font-mono">{p.phone}</span>}
                            </td>

                            <td className="px-6 py-4 text-center">
                              <StatusChip
                                label={p.status}
                                variant={p.status === 'COMPLETED' ? 'success' : 'warning'}
                              />
                            </td>

                            <td className="px-6 py-4 text-center">
                              <div className="flex flex-col gap-1 w-28 mx-auto">
                                <span className="text-[10px] font-mono font-bold text-primary">
                                  {p.progress}%
                                </span>
                                <div className="h-1.5 w-full bg-surface-container-high rounded-full overflow-hidden">
                                  <div
                                    className={`h-full transition-all duration-500 ${
                                      p.progress === 100 ? 'bg-success' : 'bg-primary'
                                    }`}
                                    style={{ width: `${p.progress}%` }}
                                  />
                                </div>
                              </div>
                            </td>

                            <td className="px-6 py-4 text-center">
                              <div className="font-mono font-bold text-on-surface">
                                {p.averageScore != null ? `${p.averageScore}%` : '-'}
                              </div>
                              <span className="text-[9px] font-bold text-primary block">
                                {letter} • {language === 'ar' ? pred.arabic : pred.indonesian}
                              </span>
                            </td>

                            <td className="px-6 py-4 text-center">
                              <button
                                type="button"
                                onClick={() =>
                                  setSelectedModalStudent({
                                    userId: p.id,
                                    programId: selectedProgramId,
                                  })
                                }
                                className="px-3 py-1.5 rounded-xl bg-primary/10 hover:bg-primary/20 text-primary font-headline font-bold text-[10px] uppercase tracking-wider flex items-center gap-1.5 mx-auto transition-colors"
                              >
                                <FileCheck size={13} />
                                <span>
                                  <Bilingual {...copy.reporting.viewTranscriptModal} />
                                </span>
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                {filteredParticipants.length === 0 && (
                  <div className="p-16 text-center text-on-surface-variant font-body italic">
                    <Bilingual {...copy.reporting.emptyMatrix} />
                  </div>
                )}

                {/* Table Pagination Bar */}
                {filteredParticipants.length > 0 && (
                  <div className="px-6 sm:px-8 py-4 border-t border-outline-variant/10 bg-surface-container-low/20 flex flex-col sm:flex-row items-center justify-between gap-4">
                    <div className="flex items-center gap-3 text-xs text-on-surface-variant">
                      <span className="font-body">
                        {t(copy.reporting.showingRows)
                          .replace('{from}', String((currentPage - 1) * pageSize + 1))
                          .replace(
                            '{to}',
                            String(Math.min(currentPage * pageSize, filteredParticipants.length))
                          )
                          .replace('{total}', String(filteredParticipants.length))}
                      </span>

                      <div className="flex items-center gap-1.5 ml-2">
                        <label className="text-[11px] font-label uppercase font-bold text-on-surface-variant/70">
                          <Bilingual {...copy.reporting.rowsPerPage} />
                        </label>
                        <select
                          value={pageSize}
                          onChange={e => {
                            setPageSize(Number(e.target.value));
                            setCurrentPage(1);
                          }}
                          aria-label="Baris per halaman"
                          className="bg-surface-container border-none text-xs font-mono font-bold rounded-lg px-2 py-1 outline-none"
                        >
                          <option value={10}>10</option>
                          <option value={25}>25</option>
                          <option value={50}>50</option>
                          <option value={100}>100</option>
                        </select>
                      </div>
                    </div>

                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        disabled={currentPage <= 1}
                        onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                        className="p-2 rounded-xl bg-surface-container hover:bg-surface-container-high disabled:opacity-40 disabled:cursor-not-allowed text-on-surface transition-colors"
                        title="Halaman Sebelumnya"
                      >
                        <ChevronLeft size={16} />
                      </button>

                      <span className="text-xs font-mono font-bold px-3 py-1 rounded-xl bg-surface-container text-on-surface">
                        {currentPage} / {totalPages}
                      </span>

                      <button
                        type="button"
                        disabled={currentPage >= totalPages}
                        onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                        className="p-2 rounded-xl bg-surface-container hover:bg-surface-container-high disabled:opacity-40 disabled:cursor-not-allowed text-on-surface transition-colors"
                        title="Halaman Berikutnya"
                      >
                        <ChevronRight size={16} />
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          ) : null}
        </>
      )}

      {/* Official Transcript Preview Modal */}
      {selectedModalStudent && (
        <TranscriptPreviewModal
          isOpen={true}
          onClose={() => setSelectedModalStudent(null)}
          programId={selectedModalStudent.programId}
          userId={selectedModalStudent.userId}
        />
      )}
    </div>
  );
}
